/**
 * Hand evaluator.
 *
 * Ported from the evaluator already running in the site's equity tool
 * (`page-tool-equity.php`, the inline `evalN`), which was verified correct here
 * by exhaustive enumeration. Two things changed in the port:
 *
 *  1. The original kept rank counts in a module-level `Int8Array` that was
 *     `fill(0)`-ed on entry. That works, but it makes the function depend on
 *     shared mutable state for no benefit. This version tracks the same
 *     information in four local bitmasks, so the evaluator allocates nothing,
 *     touches nothing outside its own frame, and is trivially safe to call from
 *     anywhere — including a Worker or a parallel test runner.
 *  2. Category names moved out. The engine returns numbers; naming them in
 *     Persian is the client's job.
 *
 * `evaluate` returns a single 32-bit integer. Higher is better, and equal means
 * a genuine tie, so `>` and `===` are the entire comparison API. The layout is:
 *
 *     bits 24..27  category (0 high card .. 8 straight flush)
 *     bits 20..23  first tiebreak rank
 *     bits 16..19  second
 *     bits 12..15  third
 *     bits  8..11  fourth
 *     bits  4..7   fifth
 *
 * Ranks are 2..14 and fit in 4 bits each, so the whole score stays inside a
 * positive signed 32-bit int and never leaves SMI range.
 */

import type { Card } from './cards.ts';

export const HIGH_CARD = 0;
export const PAIR = 1;
export const TWO_PAIR = 2;
export const TRIPS = 3;
export const STRAIGHT = 4;
export const FLUSH = 5;
export const FULL_HOUSE = 6;
export const QUADS = 7;
export const STRAIGHT_FLUSH = 8;

/** A packed hand score. Compare with `>`, `<`, `===`. */
export type HandScore = number;

/** Stable English identifiers, indexed by category. Display names live in the client. */
export const CATEGORY_NAMES = [
  'high-card',
  'pair',
  'two-pair',
  'trips',
  'straight',
  'flush',
  'full-house',
  'quads',
  'straight-flush',
] as const;

export type HandCategory = (typeof CATEGORY_NAMES)[number];

/** Index of the highest set bit, or -1 when `mask` is 0. */
function topBit(mask: number): number {
  return 31 - Math.clz32(mask);
}

/**
 * Highest card of the best straight within a rank bitmask, or 0 if there is none.
 *
 * The ace is duplicated into bit 1 so that A-2-3-4-5 (the wheel) is found by the
 * same window scan as every other straight, and reports 5 as its high card.
 */
export function straightHigh(rankMask: number): number {
  let m = rankMask;
  if (m & (1 << 14)) m |= 1 << 1;
  for (let high = 14; high >= 5; high--) {
    const window = 0b11111 << (high - 4);
    if ((m & window) === window) return high;
  }
  return 0;
}

/** Fold the top `count` ranks of `mask` into `score`, starting at `shift` and stepping down by 4. */
function packTopRanks(score: number, mask: number, count: number, shift: number): number {
  let remaining = mask;
  let out = score;
  let at = shift;
  for (let i = 0; i < count; i++) {
    const r = topBit(remaining);
    if (r < 0) break;
    out |= r << at;
    at -= 4;
    remaining &= ~(1 << r);
  }
  return out;
}

/**
 * Evaluate the best 5-card hand out of `len` cards (5, 6, or 7).
 *
 * `cards` may be longer than `len`; only the first `len` entries are read. That
 * lets callers keep one reusable 7-slot buffer and vary the board without
 * reallocating.
 */
export function evaluate(cards: readonly Card[], len: number): HandScore {
  // Ranks present at least once / twice / three times / four times.
  let rank1 = 0;
  let rank2 = 0;
  let rank3 = 0;
  let rank4 = 0;

  // Per-suit rank masks and counts, kept as locals rather than an array so the
  // hot loop stays allocation-free.
  let count0 = 0;
  let count1 = 0;
  let count2 = 0;
  let count3 = 0;
  let suitMask0 = 0;
  let suitMask1 = 0;
  let suitMask2 = 0;
  let suitMask3 = 0;

  for (let i = 0; i < len; i++) {
    const card = cards[i]!;
    const rank = (card >> 2) + 2;
    const suit = card & 3;
    const bit = 1 << rank;

    if (rank3 & bit) rank4 |= bit;
    else if (rank2 & bit) rank3 |= bit;
    else if (rank1 & bit) rank2 |= bit;
    else rank1 |= bit;

    if (suit === 0) {
      count0++;
      suitMask0 |= bit;
    } else if (suit === 1) {
      count1++;
      suitMask1 |= bit;
    } else if (suit === 2) {
      count2++;
      suitMask2 |= bit;
    } else {
      count3++;
      suitMask3 |= bit;
    }
  }

  // With at most 7 cards only one suit can reach five, so this chain is exhaustive.
  let flushMask = 0;
  if (count0 >= 5) flushMask = suitMask0;
  else if (count1 >= 5) flushMask = suitMask1;
  else if (count2 >= 5) flushMask = suitMask2;
  else if (count3 >= 5) flushMask = suitMask3;

  if (flushMask) {
    const sf = straightHigh(flushMask);
    if (sf) return (STRAIGHT_FLUSH << 24) | (sf << 20);
  }

  if (rank4) {
    const quad = topBit(rank4);
    const kicker = topBit(rank1 & ~(1 << quad));
    return (QUADS << 24) | (quad << 20) | (kicker << 16);
  }

  // Past the quads check, rank4 is empty, so rank3 is exactly-three and
  // `rank2 & ~rank3` is exactly-two.
  const tripsTop = topBit(rank3);
  if (tripsTop >= 0) {
    const tripsSecond = topBit(rank3 & ~(1 << tripsTop));
    const pairTop = topBit(rank2 & ~rank3);
    const partner = tripsSecond > pairTop ? tripsSecond : pairTop;
    if (partner >= 0) {
      return (FULL_HOUSE << 24) | (tripsTop << 20) | (partner << 16);
    }
  }

  if (flushMask) {
    return packTopRanks(FLUSH << 24, flushMask, 5, 20);
  }

  const straight = straightHigh(rank1);
  if (straight) return (STRAIGHT << 24) | (straight << 20);

  if (tripsTop >= 0) {
    return packTopRanks((TRIPS << 24) | (tripsTop << 20), rank1 & ~(1 << tripsTop), 2, 16);
  }

  const pairs = rank2;
  const pairHigh = topBit(pairs);
  if (pairHigh >= 0) {
    const pairLow = topBit(pairs & ~(1 << pairHigh));
    if (pairLow >= 0) {
      // A third pair cannot beat the top two, but its higher card is still the
      // best available kicker, which `rank1` already includes.
      const kickers = rank1 & ~(1 << pairHigh) & ~(1 << pairLow);
      return packTopRanks(
        (TWO_PAIR << 24) | (pairHigh << 20) | (pairLow << 16),
        kickers,
        1,
        12,
      );
    }
    return packTopRanks((PAIR << 24) | (pairHigh << 20), rank1 & ~(1 << pairHigh), 3, 16);
  }

  return packTopRanks(HIGH_CARD << 24, rank1, 5, 20);
}

/** Category of a score, 0..8. */
export function categoryOf(score: HandScore): number {
  return score >>> 24;
}

/** Stable identifier for a score's category. */
export function categoryNameOf(score: HandScore): HandCategory {
  return CATEGORY_NAMES[categoryOf(score)]!;
}
